import { matchPath } from 'react-router-dom'
import { CatalogueItem, ItemKind, ItemRef, Panel, Workspace } from './catalogueTypes'

export const CATEGORY_ROUTE = '/products/:categoryId?'

const ITEM_REF_PATTERN = /^(product|category)-(\d+)$/

const parseItemRef = (raw: string | null): ItemRef | null => {
  if (!raw) return null
  const match = ITEM_REF_PATTERN.exec(raw)
  if (!match) return null
  return { kind: match[1] as ItemKind, id: Number(match[2]) }
}

const itemRefParam = (ref: ItemRef): string => `${ref.kind}-${ref.id}`

export const parseWorkspace = (search: string): Workspace => {
  const params = new URLSearchParams(search)
  const newType = params.get('new')
  const edit = parseItemRef(params.get('edit'))

  let panel: Panel = { kind: 'browse' }
  if (newType === 'product' || newType === 'category') {
    panel = { kind: 'new', type: newType }
  } else if (edit) {
    panel = { kind: 'edit', item: edit }
  }

  return { panel, show: parseItemRef(params.get('show')) }
}

export const categoryIdFromPath = (pathname: string): number | null => {
  const match = matchPath<{ categoryId?: string }>(pathname, { path: CATEGORY_ROUTE })
  return match?.params.categoryId ? Number(match.params.categoryId) : null
}

export const categoryPath = (categoryId: number | null): string =>
  categoryId === null ? '/products' : `/products/${categoryId}`

const workspacePath = (categoryId: number | null, workspace: Workspace): string => {
  const params = new URLSearchParams()
  if (workspace.panel.kind === 'new') params.set('new', workspace.panel.type)
  if (workspace.panel.kind === 'edit') params.set('edit', itemRefParam(workspace.panel.item))
  if (workspace.show) params.set('show', itemRefParam(workspace.show))
  const query = params.toString()
  return query ? `${categoryPath(categoryId)}?${query}` : categoryPath(categoryId)
}

export const browsePath = (categoryId: number | null): string =>
  workspacePath(categoryId, { panel: { kind: 'browse' }, show: null })

export const newItemPath = (categoryId: number | null, type: ItemKind): string =>
  workspacePath(categoryId, { panel: { kind: 'new', type }, show: null })

export const editItemPath = (categoryId: number | null, item: ItemRef): string =>
  workspacePath(categoryId, { panel: { kind: 'edit', item }, show: null })

export const showItemPath = (categoryId: number | null, panel: Panel, item: ItemRef): string =>
  workspacePath(categoryId, { panel, show: item })

export const hideItemPath = (categoryId: number | null, panel: Panel): string =>
  workspacePath(categoryId, { panel, show: null })

export const itemRefOf = (item: CatalogueItem): ItemRef => ({
  kind: item.class_name === 'Product' ? 'product' : 'category',
  id: item.id
})

export const refersTo = (ref: ItemRef, item: CatalogueItem): boolean =>
  ref.id === item.id && ref.kind === itemRefOf(item).kind

export const panelKey = (panel: Panel): string => {
  switch (panel.kind) {
    case 'browse':
      return 'browse'
    case 'new':
      return `new-${panel.type}`
    case 'edit':
      return `edit-${itemRefParam(panel.item)}`
  }
}
